import { useCallback, useEffect } from "react";
import { useApi } from "./useApi";
import { useStateValue } from "../context/StateValueContext";
import { useCompanyId } from "./useCompanyId";
import { Brand } from "../types/paymentProcessor";
import { hasPermission } from "../functions/permissionUtils";
import { PERMISSIONS } from "../constants/permissions";

export function useBrands() {
  const { get } = useApi();
  const [{ user, brands, brandsCompanyId }, dispatch] = useStateValue();
  const { companyId } = useCompanyId();
  const isAllCompanies = hasPermission(user, PERMISSIONS.COMPANIES_READ_ALL);

  const fetchBrands = useCallback(async () => {
    try {
      let url: string;
      if (isAllCompanies) {
        if (!companyId) {
          dispatch({ type: "SET_BRANDS", brands: [], brandsCompanyId: null });
          return;
        }
        url = `/api/companies/${companyId}/brands`;
      } else {
        url = `/api/pp-accounts/brands`;
      }

      const response = await get<Brand[]>(url);
      dispatch({ type: "SET_BRANDS", brands: response, brandsCompanyId: isAllCompanies ? companyId : user!.companyId });
    } catch (err) {
      console.error("Failed to fetch brands:", err);
    }
  }, [get, dispatch, isAllCompanies, companyId, user]);

  useEffect(() => {
    if (!user) return;

    if (isAllCompanies) {
      if (!companyId) {
        if (brandsCompanyId !== null) {
          dispatch({ type: "SET_BRANDS", brands: [], brandsCompanyId: null });
        }
        return;
      }
      if (brandsCompanyId !== companyId) {
        fetchBrands();
      }
    } else if (brandsCompanyId !== user.companyId) {
      fetchBrands();
    }
  }, [user, isAllCompanies, companyId, brandsCompanyId, fetchBrands, dispatch]);

  const refreshBrands = useCallback(async () => {
    // Reset brandsCompanyId to force a re-fetch
    dispatch({ type: "SET_BRANDS", brands: [], brandsCompanyId: null });
  }, [dispatch]);

  return { brands, refreshBrands };
}
